import React, { useState, useEffect } from "react";
import {
  AddButton,
  BackButton,
  SaveButton,
} from "../../../components/btn/Buttons";
import PageHeading from "../../../components/PageHeading";
import Table from "../../../components/table/Table";
import { DeleteIcon, base_url } from "../../../utils/utils";
import Input from "../../../components/inputs/Input";
import moment from "moment";
import validatePAN from "../../../utils/validation";
import SmallModal from "../../../components/modal/SmallModal";
import { Pagination2 } from "../../../components/pagination/pagination";

const PrivateData = () => {
  const [open, setOpen] = useState(false);
  const [addProductData, setAddProductData] = useState([]);
  const [alerts, setAlerts] = useState(false);
  const [alertsMess, setAlertsMess] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [partnersListData, setPartnersListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [entries, setEntries] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  // Get agency ID from localStorage
  const getAgencyId = () => {
    const userIdString = localStorage.getItem("userId");
    if (userIdString) {
      const userData = JSON.parse(userIdString);
      return userData?.aid;
    }
    return null;
  };

  // Fetch partners list from API
  const fetchPartnersList = async (page = 1, limit = 10, search = "") => {
    try {
      setListLoading(true);
      const agencyId = getAgencyId();

      if (!agencyId) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>Error: Agency ID not found in localStorage</p>
          </div>
        );
        setListLoading(false);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append("action", "list");
      formData.append("agencyid", agencyId);
      formData.append("page", page);
      formData.append("limit", limit);
      formData.append("search", search);

      // Make API call
      const response = await fetch(
        `${base_url}financial_reports/partner_data.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        setPartnersListData(result.data);
        setTotalCount(result.totalcount || 0);
        setPageNo(page);
      } else {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "orange", textAlign: "left" }}>
            <p>{result.message || "No partners found"}</p>
          </div>
        );
        setPartnersListData([]);
      }
    } catch (error) {
      console.error("Error fetching partners list:", error);
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>Error: {error.message || "Failed to fetch partners list"}</p>
        </div>
      );
    } finally {
      setListLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchPartnersList(pageNo, parseInt(entries), "");
  }, []);

  // API call to save partner data
  const submitPartnerData = async (partnerData) => {
    try {
      setLoading(true);
      const agencyId = getAgencyId();

      if (!agencyId) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>Error: Agency ID not found in localStorage</p>
          </div>
        );
        setLoading(false);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append("action", "create");
      formData.append("agencyid", agencyId);
      formData.append("partner_name", partnerData.name);
      formData.append("emp_code", agencyId); // Using agency ID as emp_code
      formData.append("pan", partnerData.PAN);
      formData.append("share_percent", partnerData.PERC_OF_SHARE);
      formData.append("opening_balance", partnerData.OPENING_BALANCE);
      formData.append("status", partnerData.STATUS);
      formData.append("aadhar", partnerData.AADHAR || ""); // Optional field

      // Make API call
      const response = await fetch(
        `${base_url}financial_reports/partner_data.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`Partner "${partnerData.name}" saved successfully!`);
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "green", textAlign: "left" }}>
            <p>{result.message || "Partner data saved successfully!"}</p>
          </div>
        );
        return true;
      } else {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>{result.message || "Error saving partner data"}</p>
          </div>
        );
        return false;
      }
    } catch (error) {
      console.error("Error submitting partner data:", error);
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>Error: {error.message || "Failed to save partner data"}</p>
        </div>
      );
      return false;
    } finally {
      setLoading(false);
    }
  };
  const validatePAN = (pan) => {
    // PAN format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan?.toUpperCase());
  };

  const valiDate = () => {
    const errors = [];
    let count = 1;

    for (let v of addProductData) {
      // Partner Name validation
      if (!v?.name || v?.name.trim().length === 0) {
        errors.push(`Please enter Partner Name in Row ${count}`);
      }

      // PAN validation
      if (!v?.PAN || v?.PAN.trim().length === 0) {
        errors.push(`Please enter PAN in Row ${count}`);
      } else if (!validatePAN(v?.PAN)) {
        errors.push(
          `Please enter a valid PAN in Row ${count} (Format: ABCDE1234F)`
        );
      }

      // % of Share validation
      if (
        !v?.PERC_OF_SHARE ||
        v?.PERC_OF_SHARE.toString().trim().length === 0
      ) {
        errors.push(`Please enter % of Share in Row ${count}`);
      } else {
        const sharePercent = parseFloat(v?.PERC_OF_SHARE);
        if (isNaN(sharePercent)) {
          errors.push(
            `Please enter a valid number for % of Share in Row ${count}`
          );
        } else if (sharePercent <= 0 || sharePercent > 100) {
          errors.push(`% of Share must be between 0 and 100 in Row ${count}`);
        }
      }

      // Opening Balance validation
      if (
        !v?.OPENING_BALANCE ||
        v?.OPENING_BALANCE.toString().trim().length === 0
      ) {
        errors.push(`Please enter Opening Balance in Row ${count}`);
      } else {
        const balance = parseFloat(v?.OPENING_BALANCE);
        if (isNaN(balance)) {
          errors.push(
            `Please enter a valid number for Opening Balance in Row ${count}`
          );
        }
      }

      count++;
    }

    // Additional validation: Check if total share percentage equals 100%
    const totalShare = addProductData.reduce((sum, v) => {
      const share = parseFloat(v?.PERC_OF_SHARE || 0);
      return sum + (isNaN(share) ? 0 : share);
    }, 0);

    if (totalShare !== 100 && addProductData.length > 0) {
      errors.push(
        `Total % of Share must equal 100%. Current total: ${totalShare.toFixed(
          2
        )}%`
      );
    }

    return errors;
  };
  // const valiDate = () => {
  //   const errors = [];
  //   var count = 1; // Use let for consistency if modifying inside the loop
  //   for (let v of addProductData) {
  //     if (!v?.name || v?.name.trim().length === 0) {
  //       errors.push(`Please enter Partner Name in Row ${count}`);
  //     }
  //     if (!v?.PAN || v?.PAN.trim().length === 0) {
  //       errors.push(`Please enter PAN in Row ${count}`);
  //     } else if (!validatePAN(v?.PAN)) {
  //       errors.push(`Please enter a valid PAN in Row ${count}`);
  //     }
  //     if (!v?.PERC_OF_SHARE || v?.PERC_OF_SHARE.trim().length === 0) {
  //       errors.push(`Please enter % of Share in Row ${count}`);
  //     }
  //     if (!v?.OPENING_BALANCE || v?.OPENING_BALANCE.trim().length === 0) {
  //       errors.push(`Please enter Opening Balance in Row ${count}`);
  //     }
  //     count++;
  //   }
  //   return errors;
  // };

  const handleAddProduct = () => {
    const defaultProduct = {
      id: addProductData.length + 1,
      name: "",
      PAN: "",
      PERC_OF_SHARE: "",
      OPENING_BALANCE: "",
      AADHAR: "",
      STATUS: "1",
      ACTIONS: "",
    };

    if (!addProductData.length || valiDate().length === 0) {
      setAddProductData((prevData) => [...prevData, defaultProduct]);
    } else {
      // setAddProductData((prevData) => [...prevData, defaultProduct]);
      const validationErrors = valiDate();
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
    }
  };

  const handleSaveProduct = async () => {
    const validationErrors = valiDate();

    if (validationErrors.length > 0) {
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
      return;
    }

    // Save each partner to the API
    let successCount = 0;
    let failureCount = 0;

    for (const partner of addProductData) {
      const success = await submitPartnerData(partner);
      if (success) {
        successCount++;
      } else {
        failureCount++;
      }
    }

    // Show summary
    if (failureCount === 0 && successCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "green", textAlign: "left" }}>
          <p>✓ All {successCount} partner(s) saved successfully!</p>
        </div>
      );
      // Clear the form after successful save
      setTimeout(() => {
        setAddProductData([]);
        setOpen(false);
      }, 2000);
    } else if (successCount > 0 && failureCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "orange", textAlign: "left" }}>
          <p>
            ⚠ {successCount} partner(s) saved, but {failureCount} failed
          </p>
        </div>
      );
    }
  };

  const handleAddDelete = (deleteId) => {
    const filterData = addProductData.filter((item) => item.id !== deleteId);
    setAddProductData(filterData);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    fetchPartnersList(pageNumber, parseInt(entries), "");
  };

  // Handle entries change
  const handleEntriesChange = (newEntries) => {
    setPageNo(1);
    setEntries(newEntries);
    fetchPartnersList(1, parseInt(newEntries), "");
  };
  return (
    <div className="container">
      <PageHeading
        title={"PRIVATE DATA"}
        right={
          <>
            {!open && (
              <AddButton
                onClick={() => {
                  setOpen(true);
                  if (!addProductData.length) {
                    handleAddProduct();
                  }
                }}
                disabled={loading}
                style={loading ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              />
            )}
            {open && (
              <button
                className="GENERATE-BTN"
                onClick={() => {
                  setOpen(false);
                  setAddProductData([]);
                }}
                style={{ backgroundColor: "#e74c3c" }}
              >
                CANCEL
              </button>
            )}
            <SaveButton
              title={loading ? "SAVING..." : "SAVE"}
              onClick={handleSaveProduct}
              disabled={loading || !addProductData.length}
            />
          </>
        }
      />
      <Pagination2
        top={true}
        bottom={false}
        totalCount={totalCount}
        pageNo={pageNo}
        setPageNo={handlePageChange}
        entries={entries}
        setEntries={handleEntriesChange}
        search={false}
        count={5}
      />
      {open && (
        <>
          <Table
            mt={30}
            headData={[
              "NAME",
              "PAN",
              "% OF SHARE",
              "OPENING BALANCE",
              "AADHAR",
              "STATUS",
              "ACTIONS",
            ]}
            body={
              <>
                {addProductData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        placeholder={"Partner Name"}
                        value={item.name}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              name: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"PAN"}
                        value={item.PAN}
                        max={10}
                        onChange={(event) => {
                          event.target.value?.length < 11 &&
                            setAddProductData((prevData) => {
                              const updatedData = [...prevData];
                              updatedData[index] = {
                                ...updatedData[index],
                                PAN: event.target.value,
                              };
                              return updatedData;
                            });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"% OF SHARE"}
                        value={item.PERC_OF_SHARE}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              PERC_OF_SHARE: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"OPENING BALANCE"}
                        value={item.OPENING_BALANCE}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              OPENING_BALANCE: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"AADHAR"}
                        value={item.AADHAR}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              AADHAR: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={item.STATUS}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              STATUS: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                        style={{
                          padding: "8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          backgroundColor:
                            item.STATUS === "1"
                              ? "#d4edda"
                              : item.STATUS === "2"
                              ? "#f8d7da"
                              : "#fff3cd",
                          color: "#333",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                        <option value="3">Exit</option>
                      </select>
                    </td>
                    <td>
                      <DeleteIcon onClick={() => handleAddDelete(item.id)} />
                    </td>
                  </tr>
                ))}
              </>
            }
          />
        </>
      )}
      <div style={{ height: "10px" }}></div>
      <div>
        <Table
          headData={[
            "NAME",
            "PAN",
            "AADHAR",
            "% OF SHARE",
            "OPENING BALANCE",
            "STATUS",
            "CREATED DATE",
          ]}
          body={
            <>
              {partnersListData && partnersListData.length > 0 ? (
                partnersListData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.PARTNER_NAME}</td>
                    <td>{item.PAN}</td>
                    <td>{item.AADHAR}</td>
                    <td>{item.SHARE_PERCENT}%</td>
                    <td>
                      ₹{parseFloat(item.OPENING_BALANCE).toLocaleString()}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor:
                            item.STATUS === 1
                              ? "#d4edda"
                              : item.STATUS === 2
                              ? "#f8d7da"
                              : "#fff3cd",
                          color:
                            item.STATUS === 1
                              ? "#155724"
                              : item.STATUS === 2
                              ? "#721c24"
                              : "#856404",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {item.STATUS === 1
                          ? "Active"
                          : item.STATUS === 2
                          ? "Inactive"
                          : "Exit"}
                      </span>
                    </td>
                    <td>{moment(item.CREATED_DATE).format("DD/MM/YYYY")}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {listLoading ? "Loading..." : "No partners found"}
                  </td>
                </tr>
              )}
            </>
          }
        />
      </div>
      <Pagination2
        top={false}
        bottom={true}
        totalCount={totalCount}
        pageNo={pageNo}
        setPageNo={handlePageChange}
        entries={entries}
        setEntries={handleEntriesChange}
        search={false}
        count={5}
      />
      {alerts && (
        <SmallModal
          isOpen={alerts}
          setIsOpen={setAlerts}
          title={"alert"}
          mess={alertsMess}
          okbtn={setAlertsMess}
        />
      )}
    </div>
  );
};

export default PrivateData;
// import React, { useState } from "react";
// import { AddButton, BackButton, SaveButton } from "../../../components/btn/Buttons";
// import PageHeading from "../../../components/PageHeading";
// import Table from "../../../components/table/Table";
// import { DeleteIcon } from "../../../utils/utils";
// import Input from "../../../components/inputs/Input";
// import moment from "moment";
// import validatePAN from "../../../utils/validation";
// import SmallModal from "../../../components/modal/SmallModal";

// const PartnersData = () => {
//   const [open, setOpen] = useState(false);
//   const [addProductData, setAddProductData] = useState([]);
//   const [alerts, setAlerts] = useState(false);
//   const [alertsMess, setAlertsMess] = useState("");

//   const valiDate = () => {
//     const errors = [];
//     var count = 1;  // Use let for consistency if modifying inside the loop
//     for (let v of addProductData) {
//       if (!v?.name || v?.name.trim().length === 0) {
//         errors.push(`Please enter Partner Name in Row ${count}`);
//       }
//       if (!v?.PAN || v?.PAN.trim().length === 0) {
//         errors.push(`Please enter PAN in Row ${count}`);
//       } else if (!validatePAN(v?.PAN)) {
//         errors.push(`Please enter a valid PAN in Row ${count}`);
//       }
//       if (!v?.PERC_OF_SHARE || v?.PERC_OF_SHARE.trim().length === 0) {
//         errors.push(`Please enter % of Share in Row ${count}`);
//       }
//       if (!v?.OPENING_BALANCE || v?.OPENING_BALANCE.trim().length === 0) {
//         errors.push(`Please enter Opening Balance in Row ${count}`);
//       }
//       count++;
//     }
//     return errors;
//   };

//   const handleAddProduct = () => {
//     const defaultProduct = {
//       id: addProductData.length + 1,
//       NAME: "",
//       PAN: "",
//       PERC_OF_SHARE: "",
//       OPENING_BALANCE: "",
//       STATUS: "",
//       ACTIONS: "",
//     };

//     if (!addProductData.length || valiDate().length === 0) {
//       setAddProductData((prevData) => [...prevData, defaultProduct]);
//     } else {
//       // setAddProductData((prevData) => [...prevData, defaultProduct]);
//       const validationErrors = valiDate();
//       setAlerts(true);
//       setAlertsMess(
//         <div
//           style={{
//             width: "100%",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "flex-start",
//           }}
//         >
//           {validationErrors.map((item, i) => (
//             <p style={{ textAlign: "left" }} key={i}>
//               {item}
//             </p>
//           ))}
//         </div>
//       );
//     }
//   };

//   const handleSaveProduct = async () => {
//     const validationErrors = valiDate();
//     setAlerts(true);
//     setAlertsMess(
//       <div
//         style={{
//           width: "100%",
//           display: "flex",
//           flexDirection: "column",
//           justifyContent: "flex-start",
//         }}
//       >
//         {validationErrors.map((item, i) => (
//           <p style={{ textAlign: "left" }} key={i}>
//             {item}
//           </p>
//         ))}
//       </div>
//     );
//   };

//   const handleAddDelete = (deleteId) => {
//     const filterData = addProductData.filter((item) => item.id !== deleteId);
//     setAddProductData(filterData);
//   };
//   return (
//     <div className="container">
//       <PageHeading
//         title={"PARTNERS DATA"}
//         right={
//           <>
//             <AddButton
//               onClick={() => {
//                 setOpen(true);
//                 handleAddProduct();
//               }}
//             />
//             <SaveButton title={"SAVE"} onClick={handleSaveProduct} />
//           </>
//         }
//       />
//       {open && (
//         <>
//           <Table
//             mt={30}
//             headData={[
//               "NAME",
//               "PAN",
//               "% OF SHARE",
//               "OPENING BALANCE",
//               "STATUS",
//               "ACTIONS",
//             ]}
//             body={
//               <>
//                 {addProductData.map((item, index) => (
//                   <tr key={index}>
//                     <td>
//                       <Input
//                         placeholder={"Partner Name"}
//                         value={item.name}
//                         onChange={(event) => {
//                           setAddProductData((prevData) => {
//                             const updatedData = [...prevData];
//                             updatedData[index] = {
//                               ...updatedData[index],
//                               name: event.target.value,
//                             };
//                             return updatedData;
//                           });
//                         }
//                         }
//                       />
//                     </td>
//                     <td>
//                       <Input
//                         placeholder={"PAN"}
//                         value={item.PAN}
//                         onChange={(event) => {
//                           setAddProductData((prevData) => {
//                             const updatedData = [...prevData];
//                             updatedData[index] = {
//                               ...updatedData[index],
//                               PAN: event.target.value,
//                             };
//                             return updatedData;
//                           });
//                         }
//                         }
//                       />
//                     </td>
//                     <td>
//                       <Input
//                         placeholder={"% OF SHARE"}
//                         value={item.PERC_OF_SHARE}
//                         onChange={(event) => {
//                           setAddProductData((prevData) => {
//                             const updatedData = [...prevData];
//                             updatedData[index] = {
//                               ...updatedData[index],
//                               PERC_OF_SHARE: event.target.value,
//                             };
//                             return updatedData;
//                           });
//                         }
//                         }
//                       />
//                     </td>
//                     <td>
//                       <Input
//                         placeholder={"OPENING BALANCE"}
//                         value={item.OPENING_BALANCE}
//                         onChange={(event) => {
//                           setAddProductData((prevData) => {
//                             const updatedData = [...prevData];
//                             updatedData[index] = {
//                               ...updatedData[index],
//                               OPENING_BALANCE: event.target.value,
//                             };
//                             return updatedData;
//                           });
//                         }
//                         }
//                       />
//                     </td>
//                     <td></td>
//                     <td>
//                       <DeleteIcon onClick={() => handleAddDelete(item.id)} />
//                     </td>
//                   </tr>
//                 ))}
//               </>
//             }
//           />
//         </>
//       )}
//       <div style={{ height: "10px" }}></div>
//       <div>
//         <Table
//           headData={[
//             " NAME",
//             "PAN",
//             " % OF SHARE",
//             "OPENING BALANCE",
//             "STATUS",
//             "ACTIONS",
//           ]}
//           body={
//             <>
//               <tr>
//                 <td>cust yes</td>
//                 <td>0.00</td>
//                 <td>0.00</td>
//                 <td>53500</td>
//                 <td>53500</td>
//                 <td>
//                   <DeleteIcon onClick={() => handleAddDelete(0)} />
//                 </td>
//               </tr>
//             </>
//           }
//         />
//       </div>
//       {alerts && (
//         <SmallModal
//           isOpen={alerts}
//           setIsOpen={setAlerts}
//           title={"alert"}
//           mess={alertsMess}
//           okbtn={setAlertsMess}
//         />
//       )}
//     </div>
//   );
// };

// export default PartnersData;
